import { DeviceDTO } from '@eppendorf/vnls-device-service-utils';
import {
  Button,
  HasClassName,
  Icon,
  IconSizeClasses,
} from '@eppendorf/vnls-react-components';
import { useQuery } from '@tanstack/react-query';
import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAccessControlGuard } from '$shared/custom-hooks/useAccessControlGuard';
import { queryClient } from '$shared/query/query-client';

import { useContactSupport } from '$components/chat-bot/useContactSupport';
import {
  createDeviceQuery,
  devicesQueryKey,
  useReregisterDevice,
} from '$features/devices/devices.api';
import { devicesv2QueryKey } from '$features/monitoring/v2/devicesv2.api';

import styles from './device-detail-retry-reg.module.scss';

interface DeviceDetailRetryRegProps extends HasClassName {
  device: DeviceDTO;
}

export function DeviceDetailRetryReg({
  className,
  device,
}: DeviceDetailRetryRegProps): ReactElement | null {
  const { t } = useTranslation();
  const handleContactSupport = useContactSupport();
  const [retryState, setRetryState] = useState<'initital' | 'retrying' | 'retryfailed'>(
    'initital',
  );

  const { mutateAsync: reregisterDevice } = useReregisterDevice();
  const { data: deviceRes } = useQuery({
    ...createDeviceQuery({
      manufacturer: 'Eppendorf',
      serialNumber: device.serialNumber ?? '',
    }),
    enabled: retryState === 'retrying',
    refetchInterval: 1000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });
  const hasPermissionToRegisterDevice = useAccessControlGuard('ADD_DEVICE');

  async function tryAgain(): Promise<void> {
    await reregisterDevice({
      manufacturer: 'Eppendorf',
      serialNumber: device.serialNumber,
    });
    setRetryState('retrying');
  }

  useEffect(() => {
    if (deviceRes?.connectionStatus === 'paired') {
      queryClient.invalidateQueries({ queryKey: [devicesQueryKey] });
      queryClient.invalidateQueries({ queryKey: [devicesv2QueryKey] });
    }
  }, [deviceRes?.connectionStatus]);

  useEffect(() => {
    // eslint-disable-next-line no-undef -- working
    let waitForDeviceToExistTimer: NodeJS.Timeout;
    if (retryState === 'retrying') {
      waitForDeviceToExistTimer = setTimeout(() => {
        setRetryState('retryfailed');
      }, 15000);
    }

    return () => {
      clearTimeout(waitForDeviceToExistTimer);
    };
  }, [device, retryState]);

  if (device?.connectionStatus === 'paired') {
    return null;
  }

  return (
    <div className={`${styles.deviceDetailRetryReg} ${className}`}>
      <div className={styles.deviceDetailRetryRegLabel}>
        <Icon name="info" size={IconSizeClasses.XSmall} className="bg-white" />
        {retryState === 'initital' && t('addDevice.retryRegistration.hint')}
        {retryState === 'retrying' && t('addDevice.retryRegistration.pleaseWait')}
        {retryState === 'retryfailed' && (
          <div className="flex flex__dir--column">
            <div className="m-bottom-xxs">{t('addDevice.retryRegistration.hint')}</div>
            <p>
              <span>{t('errors.contactSupport')} </span>

              <button
                type="button"
                onClick={handleContactSupport}
                className="btn-as-link font-weight-regular color-white"
              >
                {t('errors.contactSupport2')}
              </button>
            </p>
          </div>
        )}
      </div>
      {retryState !== 'retrying' && hasPermissionToRegisterDevice && (
        <div className={styles.deviceDetailRetryRegAction}>
          <Button variant="tertiary" onClick={() => tryAgain()}>
            <Icon
              name="reset"
              size={IconSizeClasses.XSmall}
              className="bg-white m-right-s"
            />
            <span className={styles.deviceDetailRetryRegActionLabel}>
              {t('shared.tryAgain')}
            </span>
          </Button>
        </div>
      )}
    </div>
  );
}
